import { useEffect, useState } from 'react';
import { apiExt, isUnsupported } from '../../lib/api';
import { t } from '../../lib/i18n';
import { sx } from './styles';

const LS_IDLE = 'chatolam.idleTimeoutMin';
const IDLE_CHANGED_EVENT = 'chatolam:idle-timeout-changed';

function persistIdle(value: number): void {
  try {
    localStorage.setItem(LS_IDLE, String(value));
  } catch {
    // ignore
  }
  // Notify same-tab listeners; the native 'storage' event only fires across tabs.
  window.dispatchEvent(new CustomEvent(IDLE_CHANGED_EVENT, { detail: value }));
}

export function SecurityPanel({ canEdit }: { canEdit: boolean }) {
  // 2FA toggle removed: it was cosmetic (no working enrollment flow) and a stale
  // require2FA flag could lock agents out. Only the idle-timeout remains.
  const [idleTimeoutMin, setIdleTimeoutMin] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(LS_IDLE);
      if (stored !== null) return Math.max(0, Number(stored) || 0);
    } catch {
      // ignore
    }
    return 60;
  });
  const [saving, setSaving] = useState(false);
  const [unsupported, setUnsupported] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await apiExt.getSecurityConfig();
      if (isUnsupported(r)) {
        setUnsupported(true);
      } else {
        if (typeof r.idleTimeoutMin === 'number') setIdleTimeoutMin(r.idleTimeoutMin);
      }
      setLoaded(true);
    })().catch(() => setLoaded(true));
  }, []);

  async function save() {
    setSaving(true);
    try {
      persistIdle(idleTimeoutMin);
      const r = await apiExt.setSecurityConfig({ idleTimeoutMin });
      if (isUnsupported(r)) setUnsupported(true);
    } catch (e) {
      alert(`Save failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section>
      <h2 style={{ margin: 0, fontSize: 18, marginBottom: 12 }}>{t('settings.security.title')}</h2>
      {!loaded && <div>{t('common.loading')}</div>}
      {unsupported && <div style={{ ...sx.hint, marginBottom: 8 }}>{t('settings.security.backendMissing')}</div>}

      <div style={sx.card}>
        <label style={sx.label}>{t('settings.security.idleTimeout')}</label>
        <input
          type="number"
          min={0}
          value={idleTimeoutMin}
          onChange={(e) => setIdleTimeoutMin(Math.max(0, Number(e.target.value) || 0))}
          disabled={!canEdit}
          style={{ ...sx.input, maxWidth: 160 }}
        />
        <p style={sx.hint}>{t('settings.security.idleTimeoutHint')}</p>
      </div>

      {canEdit && (
        <button onClick={save} disabled={saving} style={sx.btnPrimary}>
          {saving ? t('common.loading') : t('common.save')}
        </button>
      )}
    </section>
  );
}

export function getStoredIdleTimeoutMin(): number {
  try {
    const stored = localStorage.getItem(LS_IDLE);
    if (stored !== null) return Math.max(0, Number(stored) || 0);
  } catch {
    // ignore
  }
  return 60;
}

// Reactive: re-reads when the stored value changes via either the same-tab
// CustomEvent we emit, or the cross-tab 'storage' event from another window.
export function useStoredIdleTimeoutMin(): number {
  const [value, setValue] = useState<number>(() => getStoredIdleTimeoutMin());
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === LS_IDLE) setValue(getStoredIdleTimeoutMin());
    };
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent<number>).detail;
      if (typeof detail === 'number') setValue(Math.max(0, detail));
      else setValue(getStoredIdleTimeoutMin());
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(IDLE_CHANGED_EVENT, onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(IDLE_CHANGED_EVENT, onCustom);
    };
  }, []);
  return value;
}
